import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppBar, Avatar, Box, Button, Chip, Container, Grid, IconButton, Skeleton,
  ToggleButton, ToggleButtonGroup, Toolbar,
  Tooltip as MuiTooltip, Typography, useTheme,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import Public from '@mui/icons-material/Public';
import Refresh from '@mui/icons-material/Refresh';
import ShowChart from '@mui/icons-material/ShowChart';
import {
  ResponsiveContainer, ComposedChart, Line, Scatter, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import DataTable, { DataTableColumn } from '../../design/DataTable';
import PanelCard from '../../design/PanelCard';
import PnlText from '../../design/PnlText';
import MarketStatusChip from '../../design/MarketStatusChip';
import { formatCurrency, formatDateTime, formatShortDate, formatCompactCurrency } from '../../design/format';
import { appBarGradient, accentColors } from '../../design/theme';
import { useAuth } from '../../context/AuthContext';
import {
  useStockDetail, useStockPrices, useStockProfile, useStockNews, prefetchStockIndicators,
  PriceRange, StockPricesResponse, StockTransaction,
} from '../../hooks/useUniverse';
import { AboutPanel, KeyStatsPanel, earningsChip } from './ProfilePanels';
import NewsPanel from './NewsPanel';
import { mergeTradesIntoPrices } from './chartMarkers';

const CHART_HEIGHT = 260;

const PRICE_RANGES: { value: PriceRange; label: string }[] = [
  { value: '1d', label: '1D' }, { value: '1w', label: '1W' }, { value: '1m', label: '1M' },
  { value: '6m', label: '6M' }, { value: '1y', label: '1Y' }, { value: '5y', label: '5Y' },
];

export const isDailyInterval = (interval: string | undefined): boolean =>
  interval === '1d' || interval === '1wk';

/**
 * Trade markers only make sense on daily/weekly rows — trade dates can't map
 * to intraday time buckets, so intraday responses render the plain line.
 */
export const buildChartRows = (prices: StockPricesResponse | null, transactions: StockTransaction[]) =>
  isDailyInterval(prices?.interval)
    ? mergeTradesIntoPrices(prices?.data ?? [], transactions)
    : prices?.data ?? [];

// Intraday labels are 'YYYY-MM-DD HH:MM' in US/Eastern (exchange-local) time and
// are shown as-is, not converted; the tooltip's "ET" suffix removes the ambiguity.
export const formatPriceTick = (label: string, interval: string | undefined): string =>
  isDailyInterval(interval) ? formatShortDate(label) : label.slice(11, 16);

export const formatPriceTooltipLabel = (label: string, interval: string | undefined): string =>
  isDailyInterval(interval)
    ? formatShortDate(label)
    // 'T' keeps the bare local datetime parseable across browsers.
    : `${formatShortDate(label.replace(' ', 'T'))} ${label.slice(11, 16)} ET`;

const TX_COLUMNS: DataTableColumn<StockTransaction>[] = [
  { key: 'Time', label: 'Time', minWidth: 150, render: (t) => formatDateTime(t.Time) },
  { key: 'TraderName', label: 'Trader', minWidth: 130, render: (t) => t.TraderName },
  {
    key: 'Action', label: 'Action', minWidth: 80,
    render: (t) => <Chip label={t.Action} color={t.Action === 'BUY' ? 'success' : 'error'} size="small" />,
  },
  { key: 'Price', label: 'Price', minWidth: 100, render: (t) => formatCurrency(t.Price) },
  { key: 'Investment', label: 'Investment', minWidth: 110, render: (t) => formatCurrency(t.Investment) },
  {
    key: 'ProfitAndLoss', label: 'P&L', minWidth: 100,
    render: (t) => (t.ProfitAndLoss === null ? '—' : <PnlText value={t.ProfitAndLoss} format="currency" variant="body2" />),
  },
];

const StockDetail: React.FC = () => {
  const { ticker = '' } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const theme = useTheme();
  const [range, setRange] = useState<PriceRange>('1y');
  const detail = useStockDetail(ticker);
  const prices = useStockPrices(ticker, range);
  const profile = useStockProfile(ticker);
  const news = useStockNews(ticker);

  // Pre-warm the expensive indicators payload (~5s cold on the server) so the
  // Indicators button opens near-instantly; the indicators page joins this
  // same promise instead of re-requesting. Failures are silent by design.
  useEffect(() => { prefetchStockIndicators(ticker); }, [ticker]);

  const interval = prices.data?.interval;
  const daily = isDailyInterval(interval);
  const chartRows = buildChartRows(prices.data, detail.data?.transactions ?? []);

  const handleBack = () => {
    // react-router tracks its position in history.state.idx; 0 = first in-app entry
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate('/universe');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const stock = detail.data?.stock;
  // useGet collapses every failure (404, 500, network) into the same generic
  // error, so a failed load can't claim "not found" — offer a retry instead.
  const loadFailed = !detail.loading && !stock;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0} sx={{ background: appBarGradient }}>
        <Toolbar>
          <IconButton color="inherit" onClick={handleBack} aria-label="Back" sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <IconButton onClick={() => navigate('/')} aria-label="Home" sx={{ p: 0, mr: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}><Public /></Avatar>
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>{ticker.toUpperCase()}</Typography>
          <Box sx={{ mr: 1 }}>
            <MarketStatusChip />
          </Box>
          <IconButton color="inherit" onClick={handleLogout} aria-label="Log out">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {detail.loading ? (
          <Skeleton variant="rounded" height={120} />
        ) : loadFailed ? (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography color="error">Could not load stock {ticker.toUpperCase()}</Typography>
            <MuiTooltip title="Retry">
              <IconButton onClick={() => detail.refetch()} color="primary" aria-label="Retry">
                <Refresh />
              </IconButton>
            </MuiTooltip>
          </Box>
        ) : stock && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: accentColors.ticker }}>
                {stock.Ticker}
              </Typography>
              <Typography variant="h6" color="text.secondary">{stock.Company ?? ''}</Typography>
              {stock.Sector && <Chip label={stock.Sector} size="small" />}
              {stock.Industry && <Chip label={stock.Industry} size="small" variant="outlined" />}
              {stock.Country && <Chip label={stock.Country} size="small" variant="outlined" />}
              {stock.BlackList && <Chip label="Delisted" color="error" size="small" />}
              {profile.data && earningsChip(profile.data.next_earnings_date)}
              <Button variant="outlined" size="small" startIcon={<ShowChart />} sx={{ ml: 'auto' }}
                      onClick={() => navigate(`/universe/${encodeURIComponent(ticker)}/indicators`)}>
                Indicators
              </Button>
            </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={12} lg={7}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <PanelCard
                    title="Price"
                    action={
                      <ToggleButtonGroup
                        value={range} exclusive size="small" aria-label="Price range"
                        onChange={(_, next: PriceRange | null) => { if (next) setRange(next); }}
                      >
                        {PRICE_RANGES.map((r) => (
                          <ToggleButton key={r.value} value={r.value}>{r.label}</ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    }
                    error={prices.error} onRetry={prices.refetch}
                  >
                    {prices.loading ? (
                      <Skeleton variant="rounded" height={CHART_HEIGHT} />
                    ) : (prices.data?.data.length ?? 0) === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                        Price data unavailable
                      </Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                        <ComposedChart data={chartRows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                          <XAxis
                            dataKey="date"
                            tickFormatter={(label: string) => formatPriceTick(label, interval)}
                            tickLine={false}
                            axisLine={false}
                            fontSize={11}
                            interval="preserveStartEnd"
                            minTickGap={40}
                            stroke={theme.palette.text.secondary}
                          />
                          <YAxis
                            tickFormatter={formatCompactCurrency}
                            domain={['auto', 'auto']}
                            tickLine={false}
                            axisLine={false}
                            fontSize={11}
                            width={48}
                            stroke={theme.palette.text.secondary}
                          />
                          <Tooltip
                            formatter={(v: number) => formatCurrency(v)}
                            labelFormatter={(label: string) => formatPriceTooltipLabel(label, interval)}
                            cursor={{ stroke: theme.palette.divider }}
                          />
                          <Line type="monotone" dataKey="close" name="Close" stroke={accentColors.ticker}
                                strokeWidth={2} dot={false} />
                          {daily && (
                            <Scatter dataKey="buyPrice" name="Buy"
                                     fill={theme.palette.success.main} shape="triangle" />
                          )}
                          {daily && (
                            <Scatter dataKey="sellPrice" name="Sell"
                                     fill={theme.palette.error.main} />
                          )}
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                  </PanelCard>

                  <NewsPanel news={news} />
                </Box>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <AboutPanel profile={profile} />
                  <KeyStatsPanel profile={profile} />

                  <PanelCard title="Traded by">
                    {(detail.data!.summary.per_trader.length === 0) ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                        No trader has traded this stock yet
                      </Typography>
                    ) : (
                      detail.data!.summary.per_trader.map((t) => (
                        <Box key={t.trader_id}
                             sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                          <Typography variant="body2">{t.name}</Typography>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary">{t.trades} trades</Typography>
                            <PnlText value={t.pnl} format="currency" variant="body2" />
                          </Box>
                        </Box>
                      ))
                    )}
                  </PanelCard>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <PanelCard title="Trade history (all traders)">
                  <DataTable
                    columns={TX_COLUMNS} rows={detail.data!.transactions}
                    rowKey={(t) => t.Id} emptyMessage="No transactions for this stock"
                  />
                </PanelCard>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </Box>
  );
};

export default StockDetail;
